import { useEffect } from 'react'

/**
 * Writes a service page's head, in the order the pre-redesign pages did:
 *   title → meta description → [keywords] → [og:* by property]
 *   → [twitter:* by name] → [Service JSON-LD, once per id]
 * then fills in, from `fallback`, any Open Graph / Twitter tag the page's own
 * block leaves out (EN and DE never carried them), so every service page
 * shares one complete set. index.html carries none of these tags, and the
 * canonical is CanonicalManager's, so each exists once.
 * Only tags without Helmet's data-rh are touched: a Helmet page's tags, about
 * to be removed by Helmet, are never adopted. On unmount it removes every
 * tag it wrote and restores the locale's default title (unless the next page
 * already set its own).
 *
 * @param {{
 *   title: string,
 *   description: string,
 *   keywords?: string,
 *   og?: Record<string, string>,
 *   twitter?: Record<string, string>,
 *   schemaId?: string,
 *   schema?: object,
 * }} seo - a frozen module constant from src/data/services/*.
 * @param {string} resetTitle - document.title restored on unmount.
 * @param {{ url: string, image: string, locale: string }} fallback - absolute
 *   page URL, absolute 1200 x 630 image, og:locale.
 */
export default function useServiceSeo(seo, resetTitle, fallback) {
  const { url, image, locale } = fallback
  useEffect(() => {
    document.title = seo.title

    const written = [
      upsertMeta('name', 'description', seo.description),
      seo.keywords ? upsertMeta('name', 'keywords', seo.keywords) : null,
      ...Object.entries(seo.og ?? {}).map(([property, content]) => upsertMeta('property', property, content)),
      ...Object.entries(seo.twitter ?? {}).map(([name, content]) => upsertMeta('name', name, content)),
    ]

    const ogDefaults = {
      'og:type': 'website',
      'og:url': url,
      'og:title': seo.title,
      'og:description': seo.description,
      'og:image': image,
      'og:image:width': '1200',
      'og:image:height': '630',
      'og:locale': locale,
      'og:site_name': 'Ankaplastik',
    }
    const twitterDefaults = {
      'twitter:card': 'summary_large_image',
      'twitter:title': seo.title,
      'twitter:description': seo.description,
      'twitter:image': image,
    }
    const filled = [
      ...Object.entries(ogDefaults)
        .filter(([property]) => !seo.og?.[property])
        .map(([property, content]) => upsertMeta('property', property, content)),
      ...Object.entries(twitterDefaults)
        .filter(([name]) => !seo.twitter?.[name])
        .map(([name, content]) => upsertMeta('name', name, content)),
    ]

    const schemaSelector = seo.schema ? `script[data-schema="${seo.schemaId}"]` : null
    if (schemaSelector && !document.querySelector(schemaSelector)) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-schema', seo.schemaId)
      script.textContent = JSON.stringify(seo.schema)
      document.head.appendChild(script)
    }

    return () => {
      // React may flush this cleanup after the next page's Helmet has set its
      // own title: reset only a title that is still this page's.
      if (document.title === seo.title) document.title = resetTitle
      ;[...written, ...filled].forEach((tag) => tag?.remove())
      if (schemaSelector) document.querySelector(schemaSelector)?.remove()
    }
  }, [seo, resetTitle, url, image, locale])
}

/**
 * @param {'name' | 'property'} attr
 * @param {string} key
 * @param {string} content
 * @returns {HTMLMetaElement}
 */
function upsertMeta(attr, key, content) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]:not([data-rh])`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.content = content
  return tag
}
